import { useState, useEffect } from "react";
import { ArrowLeft, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getCookieConsent, saveCookieConsent, loadAnalytics, loadAdvertising } from "@/components/CookieBanner";

export default function CookieSettingsPage() {
  const [analytics, setAnalytics] = useState(false);
  const [advertising, setAdvertising] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const consent = getCookieConsent();
    if (consent) {
      setAnalytics(consent.analytics || false);
      setAdvertising(consent.advertising || false);
    }
  }, []);

  const handleSave = () => {
    const consent = { essential: true, analytics, advertising };
    saveCookieConsent(consent);
    if (analytics) loadAnalytics();
    if (advertising) loadAdvertising();
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleRejectAll = () => {
    setAnalytics(false);
    setAdvertising(false);
    saveCookieConsent({ essential: true, analytics: false, advertising: false });
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-2xl mx-auto px-6 py-16">
        <a href="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-orange-600 mb-8">
          <ArrowLeft size={14} /> Back to AdBit
        </a>

        <div className="flex items-center gap-3 mb-2">
          <Shield size={24} className="text-orange-500" />
          <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: "Outfit, sans-serif" }}>Cookie Settings</h1>
        </div>
        <p className="text-sm text-slate-500 mb-8">Manage how we use cookies and tracking technologies on this site. For more information, read our <a href="/privacy" className="text-orange-600 hover:underline">Privacy Policy</a>.</p>

        {saved && (
          <div className="mb-6 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl">
            <p className="text-sm text-emerald-700 font-medium">Preferences saved successfully.</p>
          </div>
        )}

        <div className="space-y-4 mb-8">
          {/* Essential */}
          <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-slate-800">Essential Cookies</h3>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">Required for authentication and core platform functionality. These cookies are strictly necessary and cannot be disabled.</p>
              </div>
              <div className="w-12 h-6 bg-orange-500 rounded-full flex items-center justify-end px-0.5 flex-shrink-0">
                <div className="w-5 h-5 bg-white rounded-full" />
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-slate-200">
              <p className="text-[10px] text-slate-400">Cookies used: Authentication token (JWT), session management</p>
            </div>
          </div>

          {/* Analytics */}
          <div className="p-5 bg-white rounded-2xl border border-slate-200 cursor-pointer" onClick={() => setAnalytics(!analytics)}>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-slate-800">Analytics Cookies</h3>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">Google Analytics — helps us understand how visitors interact with the site, which pages are most visited, and where traffic comes from. Microsoft Clarity — records user sessions and generates heatmaps to help us improve the user experience. Both collect data in anonymized or pseudonymized form.</p>
              </div>
              <div className={`w-12 h-6 rounded-full flex items-center px-0.5 transition-colors flex-shrink-0 ${analytics ? "bg-orange-500 justify-end" : "bg-slate-300 justify-start"}`}>
                <div className="w-5 h-5 bg-white rounded-full" />
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-slate-200">
              <p className="text-[10px] text-slate-400">Google Analytics · Provider: Google LLC · Retention: up to 26 months · <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:underline">Google Privacy Policy</a></p>
              <p className="text-[10px] text-slate-400 mt-1">Microsoft Clarity · Provider: Microsoft Corporation · <a href="https://privacy.microsoft.com/en-us/privacystatement" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:underline">Microsoft Privacy Statement</a></p>
            </div>
          </div>

          {/* Advertising */}
          <div className="p-5 bg-white rounded-2xl border border-slate-200 cursor-pointer" onClick={() => setAdvertising(!advertising)}>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-slate-800">Advertising Cookies</h3>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">Meta Pixel and Conversion API — used to measure advertising effectiveness, optimize ad campaigns, and build audiences for future advertising. These tools may track behavior across websites and devices.</p>
              </div>
              <div className={`w-12 h-6 rounded-full flex items-center px-0.5 transition-colors flex-shrink-0 ${advertising ? "bg-orange-500 justify-end" : "bg-slate-300 justify-start"}`}>
                <div className="w-5 h-5 bg-white rounded-full" />
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-slate-200">
              <p className="text-[10px] text-slate-400">Provider: Meta Platforms Inc. · <a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:underline">Meta Privacy Policy</a></p>
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <Button onClick={handleSave} className="gradient-btn rounded-xl h-10 px-6 text-sm font-semibold">
            Save Preferences
          </Button>
          <Button onClick={handleRejectAll} variant="outline" className="rounded-xl h-10 px-6 text-sm font-semibold border-slate-200">
            Reject All Non-Essential
          </Button>
        </div>

        <p className="text-[10px] text-slate-400 mt-6 leading-relaxed">
          You can change your cookie preferences at any time by returning to this page. 
          Non-essential cookies will not be loaded until you provide consent. 
          For more details about how we process your data, please review our <a href="/privacy" className="text-orange-500 hover:underline">Privacy Policy</a>.
        </p>
      </div>
    </div>
  );
}
